import {
  AttachFile as AttachFileIcon,
  CalendarToday as CalendarIcon,
  ChevronLeft as ChevronLeftIcon,
  DeviceHub as DeviceIcon,
  Email as EmailIcon,
  Fingerprint as FingerprintIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useState } from 'react';

const ParticipantDetailsCollapsible = ({
  participantData,
  defaultExpanded = false,
  onExpandChange,
  turnStats,
  assignedTags,
  canEdit,
  onAssignTagClick,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleExpandClick = () => {
    const newState = !expanded;
    setExpanded(newState);
    if (onExpandChange) {
      onExpandChange(newState);
    }
  };

  function transformApiResponse(apiResponse) {
    return {
      participantType: apiResponse?.ai_generated ? 'AI' : 'Human',
      email: apiResponse?.email || null,
      trackerId: apiResponse?.recruitment_provider_iid || null,
      location: apiResponse?.location || null,
      device: apiResponse?.device || null,
      submissionDate: apiResponse?.submitting_date || null,
      // For uploaded transcripts - original filename and content type
      originalFilename: apiResponse?.original_filename || null,
      fileContentType: apiResponse?.file_content_type || null,
    };
  }

  const ParticipantDataTransformed = transformApiResponse(participantData || {});
  const data = ParticipantDataTransformed;

  // Helper to determine if a field should be shown
  const hasValue = (value) => value && value !== '--' && value !== '';

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'GMT',
    });
  };

  const tags = Array.isArray(assignedTags) ? assignedTags : [];

  return (
    <Tooltip title={expanded ? '' : 'Expand Metadata'} placement="left">
      <Card
        variant="outlined"
        onClick={!expanded ? handleExpandClick : undefined}
        sx={{
          p: 0,
          cursor: !expanded ? 'pointer' : 'default',
          width: expanded ? '100%' : 48,
          minWidth: expanded ? 280 : 48,
          minHeight: expanded ? 'auto' : 120,
          overflow: 'hidden',
          transition: 'width 0.3s ease, min-width 0.3s ease, min-height 0.3s ease',
          '&:hover': !expanded
            ? {
                backgroundColor: 'rgba(0,0,0,0.04)',
              }
            : {},
        }}
      >
        {/* Collapsed view - vertical strip */}
        <Box
          sx={{
            display: expanded ? 'none' : 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            py: 2,
            px: 1,
            height: '100%',
            minHeight: 120,
          }}
        >
          <PersonIcon color="primary" />
          <IconButton size="small">
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Expanded view - full panel */}
        <Box
          sx={{
            display: expanded ? 'block' : 'none',
            opacity: expanded ? 1 : 0,
            transition: 'opacity 0.2s ease 0.1s',
          }}
        >
          <Box
            onClick={handleExpandClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="primary" sx={{ fontSize: 20 }} />
              <Typography
                variant="subtitle2"
                component="div"
                sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
              >
                Metadata
              </Typography>
              <Chip
                label={data.participantType}
                color="primary"
                size="small"
                sx={{ ml: 0.5, height: 22 }}
              />
            </Box>

            <Tooltip title="Collapse">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleExpandClick();
                }}
                aria-expanded={expanded}
                aria-label="collapse"
                size="small"
                sx={{
                  transform: 'rotate(180deg)',
                }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Divider />
          <CardContent sx={{ pt: 1.5, pb: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Original Filename - for uploaded transcripts */}
              {hasValue(data?.originalFilename) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachFileIcon color="action" sx={{ fontSize: 16 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
                    File:
                  </Typography>
                  <Tooltip title={data?.originalFilename} placement="top">
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 500,
                        wordBreak: 'break-all',
                        maxWidth: 180,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {data?.originalFilename}
                    </Typography>
                  </Tooltip>
                </Box>
              )}

              {/* Email - only show if present */}
              {hasValue(data?.email) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon color="action" sx={{ fontSize: 16 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
                    Email:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500, wordBreak: 'break-all' }}>
                    {data?.email}
                  </Typography>
                </Box>
              )}

              {/* Tracker ID - only show if present */}
              {hasValue(data?.trackerId) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FingerprintIcon color="action" sx={{ fontSize: 16 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
                    Tracker ID:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500, wordBreak: 'break-all' }}>
                    {data?.trackerId}
                  </Typography>
                </Box>
              )}

              {/* Location - only show if present */}
              {hasValue(data?.location) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationIcon color="action" sx={{ fontSize: 16 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
                    Location:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {data?.location}
                  </Typography>
                </Box>
              )}

              {/* Device - only show if present */}
              {hasValue(data?.device) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DeviceIcon color="action" sx={{ fontSize: 16 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
                    Device:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {data?.device}
                  </Typography>
                </Box>
              )}

              {/* Submitted Date - only show if present */}
              {hasValue(data?.submissionDate) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon color="action" sx={{ fontSize: 16 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
                    Submitted:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {formatDate(data.submissionDate)}
                  </Typography>
                </Box>
              )}

              {/* Turn Statistics */}
              {turnStats && turnStats.totalTurns > 0 && (
                <>
                  <Divider sx={{ my: 0.5 }} />
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ textAlign: 'center', flex: 1, minWidth: 50 }}>
                      <Typography variant="subtitle2" color="primary" fontWeight="bold">
                        {turnStats.totalTurns}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.65rem' }}
                      >
                        Turns
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', flex: 1, minWidth: 50 }}>
                      <Typography variant="subtitle2" color="primary" fontWeight="bold">
                        {turnStats.aiTurns}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.65rem' }}
                      >
                        AI
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', flex: 1, minWidth: 50 }}>
                      <Typography variant="subtitle2" color="primary" fontWeight="bold">
                        {turnStats.humanTurns}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.65rem' }}
                      >
                        Human
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', flex: 1, minWidth: 50 }}>
                      <Typography variant="subtitle2" color="primary" fontWeight="bold">
                        {turnStats.averageWordsPerTurn}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.65rem' }}
                      >
                        Avg W/T
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}

              {/* Tags */}
              {(tags.length > 0 || canEdit) && (
                <>
                  <Divider sx={{ my: 0.5 }} />
                  <Box
                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, flexWrap: 'wrap' }}
                  >
                    {canEdit && (
                      <Tooltip title="Assign tags">
                        <IconButton size="small" onClick={onAssignTagClick} sx={{ p: 0.25 }}>
                          <Icon icon="streamline-plump:tag-alt-remix" width="14" height="14" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {tags.map((tag) => (
                      <Chip
                        key={tag.tag_key_id}
                        label={`${tag.tag_key}: ${tag.values?.map((v) => v.value_key).join(', ')}`}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          bgcolor: '#f3e7ff',
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                    ))}
                    {tags.length === 0 && !canEdit && (
                      <Typography variant="caption" color="text.secondary">
                        No tags
                      </Typography>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </CardContent>
        </Box>
      </Card>
    </Tooltip>
  );
};

export default ParticipantDetailsCollapsible;
